<template>
    <button
        type="button"
        :class="buttonClasses"
        :disabled="disabled || loading"
        :title="title"
        :aria-label="title || (typeof $slots.default !== 'undefined' ? undefined : icon)"
        v-bind="$attrs"
        @click="$emit('click', $event)"
    >
        <span v-if="loading" class="carbon-btn-spinner">
            <i class="fas fa-spinner fa-spin"></i>
        </span>
        <i v-else-if="icon" :class="iconClass"></i>
        <slot v-if="!loading"></slot>
    </button>
</template>

<script>
export default {
    name: "CarbonButton",
    inheritAttrs: false,
    props: {
        /** 스타일 변형: default | primary | secondary | search | preset | add | clear | danger | bulk | fileSelect | upload */
        variant: {
            type: String,
            default: "default",
            validator: (v) =>
                [
                    "default",
                    "primary",
                    "secondary",
                    "search",
                    "preset",
                    "add",
                    "clear",
                    "danger",
                    "bulk",
                    "fileSelect",
                    "upload",
                ].includes(v),
        },
        /** 아이콘만 보일 때(텍스트 없음) 정사각형 버튼 */
        iconOnly: {
            type: Boolean,
            default: false,
        },
        /** FontAwesome 아이콘 클래스 (예: 'fas fa-plus') */
        icon: {
            type: String,
            default: "",
        },
        /** 툴팁/접근성 */
        title: {
            type: String,
            default: "",
        },
        disabled: {
            type: Boolean,
            default: false,
        },
        loading: {
            type: Boolean,
            default: false,
        },
        /** 추가 클래스 */
        customClass: {
            type: [String, Array],
            default: "",
        },
    },
    computed: {
        iconClass() {
            const base = (this.icon || "").trim();
            return base || "";
        },
        buttonClasses() {
            const list = ["carbon-btn"];
            list.push(`carbon-btn--${this.variant}`);
            if (this.iconOnly) list.push("carbon-btn--icon-only");
            const custom = this.customClass;
            if (custom) {
                if (Array.isArray(custom)) list.push(...custom.filter(Boolean));
                else list.push(custom);
            }
            return list;
        },
    },
};
</script>

<style scoped>
.carbon-btn {
    padding: 6px 12px;
    border: 1px solid #cbd5e1;
    border-radius: 4px;
    font-size: 12px;
    font-weight: 500;
    cursor: pointer;
    background: #ffffff;
    color: #64748b;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    transition: all 0.2s;
    white-space: nowrap;
}

.carbon-btn:focus {
    outline: none;
}

.carbon-btn:hover:not(:disabled) {
    background: #f1f5f9;
    border-color: #94a3b8;
}

.carbon-btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}

.carbon-btn--icon-only {
    min-width: 32px;
    width: 32px;
    height: 32px;
    padding: 0;
}

.carbon-btn--primary,
.carbon-btn--add,
.carbon-btn--search {
    background: #52b788;
    color: #ffffff;
    border-color: #52b788;
}

.carbon-btn--primary:hover:not(:disabled),
.carbon-btn--add:hover:not(:disabled),
.carbon-btn--search:hover:not(:disabled),
.carbon-btn--fileSelect:hover:not(:disabled),
.carbon-btn--upload:hover:not(:disabled) {
    background: #2d8659;
    border-color: #2d8659;
}

.carbon-btn--bulk {
    background: #f0fdf4;
    color: #166534;
    border: 1px solid #86efac;
    font-weight: 600;
}

.carbon-btn--bulk:hover:not(:disabled) {
    background: #dcfce7;
    border-color: #4ade80;
}

.carbon-btn--fileSelect,
.carbon-btn--upload {
    background: #52b788;
    color: #ffffff;
    border-color: #52b788;
}

.carbon-btn--preset {
    background: #f0fdf4;
    color: #16a34a;
    border-color: #86efac;
}

.carbon-btn--preset:hover:not(:disabled) {
    background: #dcfce7;
    border-color: #4ade80;
}

.carbon-btn--clear {
    padding: 6px 10px;
    font-size: 11px;
}

.carbon-btn--danger {
    color: #dc2626;
}

.carbon-btn--danger:hover:not(:disabled) {
    background: #fef2f2;
    border-color: #dc2626;
    color: #dc2626;
}

.carbon-btn-spinner {
    display: inline-flex;
}

.carbon-btn i {
    font-size: inherit;
}
</style>
